import React from 'react';

const TONE_CLASSES = {
  success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  danger: 'bg-rose-100 text-rose-800 border-rose-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
};

function chipClass(tone) {
  return `inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${TONE_CLASSES[tone] || TONE_CLASSES.neutral}`;
}

function fallbackFromBooking(booking) {
  const st = (booking?.status || 'pending').toLowerCase();
  const pay = (booking?.payment_status || '').toLowerCase();
  const paymentLabel = pay === 'paid' || ['confirmed', 'success'].includes(st) ? 'Paid' : pay === 'refunded' ? 'Refunded' : 'Pending';
  const paymentTone = paymentLabel === 'Paid' ? 'success' : paymentLabel === 'Refunded' ? 'neutral' : 'warning';
  let tripLabel = 'Processing';
  let tripTone = 'warning';
  if (st === 'cancelled') {
    tripLabel = 'Cancelled';
    tripTone = 'neutral';
  } else if (st === 'failed') {
    tripLabel = 'Failed';
    tripTone = 'danger';
  } else if (['on_hold', 'hold'].includes(st)) {
    tripLabel = 'On hold';
    tripTone = 'info';
  } else if (['confirmed', 'success', 'ticketed'].includes(st) && booking?.pnr) {
    tripLabel = 'Ticketed';
    tripTone = 'success';
  }
  return {
    payment: { code: 'payment', label: paymentLabel, tone: paymentTone },
    trip: { code: 'trip', label: tripLabel, tone: tripTone },
    booking: { code: 'booking', label: st.replace(/_/g, ' '), tone: tripTone },
    ticket_detail: '',
  };
}

export default function BookingStatusChips({
  consumerStatus,
  booking,
  layout = 'list',
  className = '',
}) {
  const cs = consumerStatus || booking?.consumer_status || fallbackFromBooking(booking);
  if (!cs?.payment || !cs?.trip) return null;

  if (layout === 'detail') {
    return (
      <div className={`space-y-4 ${className}`} data-testid="booking-status-detail">
        <div>
          <p className="text-sm text-gray-500 mb-1">Payment status</p>
          <span className={chipClass(cs.payment.tone)} data-testid="consumer-payment-status">
            {cs.payment.label}
          </span>
        </div>
        <div>
          <p className="text-sm text-gray-500 mb-1">Booking status</p>
          <span className={chipClass(cs.booking?.tone || 'neutral')} data-testid="consumer-booking-status">
            {cs.booking?.label || '—'}
          </span>
        </div>
        <div>
          <p className="text-sm text-gray-500 mb-1">Ticket status</p>
          <span className={chipClass(cs.trip.tone)} data-testid="consumer-ticket-status">
            {cs.trip.label}
          </span>
          {cs.ticket_detail && cs.ticket_detail !== cs.trip.label && (
            <p className="text-xs text-gray-500 mt-1 font-mono">{cs.ticket_detail}</p>
          )}
          {cs.trip.supplier_raw && (
            <p className="text-[10px] text-gray-400 mt-0.5">Supplier: {cs.trip.supplier_raw}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`} data-testid="booking-status-chips">
      <span className={chipClass(cs.payment.tone)} title="Payment">
        Pay: {cs.payment.label}
      </span>
      <span className={chipClass(cs.trip.tone)} title="Trip / ticket">
        Trip: {cs.trip.label}
      </span>
    </div>
  );
}
